import { Component } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import WorkRequestHelper from 'app/managecustomer/WorkRequestHelper';
import { PreAuditDetailService } from '../../../services/pre-audit-detail.service';
import { Position } from '../../../shared/models/position';
import { SessionStorageService } from '../../../services/session-storage.service';
import Utils from '../../../shared/utils';
import OnsiteCopyPageTypeFactory from '../../../factories/onsite-copy-page-type.factory';
import { CustomerKeyValuePairService } from '../../../services/customer-keyvaluepair.service';
import CustomerHelper from 'app/managecustomer/CustomerHelper';

@Component({
  selector: 'app-components-managecustomer-onsitecopycustomaction',
  templateUrl: './OnsiteCopyCustomAction.component.html'
})
export class OnsiteCopyCustomActionComponent {
  private onsiteCopyForm: UntypedFormGroup;
  Position = Position;
  dfd: any;

  canEdit = true;
  isNew = true;
  isCmsNotSet: boolean;
  workRequestDetail: Boo.Objects.Work.SeoMinute.WorkRequestDetail;
  selectedWebsiteUrl: Boo.Objects.WebsiteUrl;
  hasValidAccessDetails = false;
  implementationOwner: Boo.Objects.Enums.ImplementationOwners;
  boostToPublish: boolean;

  user: any;
  pageTypes: any = ko.observableArray(this.onsiteCopyPageTypeFactory.getPageTypes());
  validation: any = ko.validatedObservable();

  publishToExistingPage: UntypedFormControl = new UntypedFormControl(null, Validators.required);
  linkBuildingAfterPublish: KnockoutObservable<boolean> = ko.observable();
  onsiteCopyPageType: KnockoutObservable<string> = ko.observable();
  implementationInstructions: KnockoutObservable<string> = ko.observable();
  customDescription: KnockoutObservable<string> = ko.observable();

  get activeUrl(): Boo.Objects.WebsiteUrl {
    return this.selectedWebsiteUrl && this.selectedWebsiteUrl.WebsiteUrlId ? this.selectedWebsiteUrl : null;
  }

  get hasActiveKeyword(): boolean {
    return this.activeUrl && this.activeUrl.Keywords.length > 0;
  };

  constructor(
    private preAuditDetailService: PreAuditDetailService,
    private customerKeyValuePairService: CustomerKeyValuePairService,
    private sessionStorageService: SessionStorageService,
    private onsiteCopyPageTypeFactory: OnsiteCopyPageTypeFactory) { }

  activate(options: any): any {
    if (options.controlViewModel) {
      options.controlViewModel(this);
    }

    this.dfd = options.parentWindowDfd;

    this.workRequestDetail = options.workRequestDetail;
    this.customDescription(WorkRequestHelper.getCustomDescription(this.workRequestDetail.WorkRequest));
    this.selectedWebsiteUrl = options.selectedWebsiteUrl;
    this.canEdit = options.canEdit();
    this.isNew = options.isNew;
    this.hasValidAccessDetails = options.hasValidAccessDetails;

    return $.when<any>(
        Utils.wrapDfd(this.sessionStorageService.getUser()),
        Utils.wrapDfd(this.preAuditDetailService.getByCustomerId(this.workRequestDetail.WorkRequest.CustomerId)),
        Utils.wrapDfd(this.sessionStorageService.getStaticData()),
        Utils.wrapDfd(this.customerKeyValuePairService.get(this.workRequestDetail.WorkRequest.CustomerId)))
      .then((loggedInUser: any, preAudit: Boo.Objects.PreAuditDetail, staticData: Boo.Objects.LaunchPadStaticData, customerKeyValuePairs: Boo.Objects.CustomerKeyValuePair[]) => {
        this.isCmsNotSet = !preAudit || (preAudit && !preAudit.ContentManagementSystemId);

        this.user = loggedInUser;
        this.implementationOwner = CustomerHelper.getNumber(customerKeyValuePairs, Boo.Objects.Enums.KeyEnum.ImplementationOwner) || Boo.Objects.Enums.ImplementationOwners.NotSelected;
        this.boostToPublish = this.implementationOwner === Boo.Objects.Enums.ImplementationOwners.Boostability;

        this.completeActivation();
      }).fail((displayMessage) => {
        toastr.error(displayMessage);
      });
  }

  selectedUrlChanged(newUrl: Boo.Objects.WebsiteUrl): void {
    this.selectedWebsiteUrl = newUrl;
    this.linkBuildingAfterPublish(this.selectedWebsiteUrl && this.selectedWebsiteUrl.StatusId === Boo.Objects.Enums.WebsiteUrlStatuses.Target ? true : undefined);
  }

  private completeActivation(): void {
    this.onsiteCopyForm = new UntypedFormGroup({
      publishToExistingPage: this.publishToExistingPage
    });

    if (!this.canEdit) {
      this.onsiteCopyForm.disable();
    }
    
    this.implementationInstructions.extend({
      required: {
        onlyIf: (): boolean => {
          return this.boostToPublish;
        },
        maxLength: 4000,
        message: 'Implementation instructions are required because Boostability will publish copy.'
      }
    })

    this.customDescription.extend({
      required: true,
      validation: { validator: Utils.containsNoHtml, message: 'Html tags are not allowed in the description. Please upload a file if html is needed for this work item.' },
      maxLength: 3000,
    });

    this.onsiteCopyPageType.extend({ required: true, maxLength: 4000 });
    this.validation = ko.validatedObservable({
      LinkBuild: this.linkBuildingAfterPublish,
      PageType: this.onsiteCopyPageType,
      ImplementationInstructions: this.implementationInstructions,
      CustomDescription: this.customDescription
    });

    this.selectedUrlChanged(this.selectedWebsiteUrl);

    if (this.workRequestDetail && this.workRequestDetail.WorkRequest.KeyValuePairs && !this.isNew) {
      this.publishToExistingPage.setValue(WorkRequestHelper.getOnsiteCopyOrderReason(this.workRequestDetail.WorkRequest).indexOf('Existing Page') !== -1);
      this.linkBuildingAfterPublish(WorkRequestHelper.getOnsiteCopyLinkBuildAfterPublish(this.workRequestDetail.WorkRequest));
      this.onsiteCopyPageType(WorkRequestHelper.getOnsiteCopyPageType(this.workRequestDetail.WorkRequest));
      this.implementationInstructions(WorkRequestHelper.getOnsiteCopyImplementationInstructions(this.workRequestDetail.WorkRequest));
    }

    this.validation.errors.showAllMessages(false);
  }

  canSave(): any {
    Utils.showAllMessages(this.onsiteCopyForm);
    let result = this.validation.isValid() && this.onsiteCopyForm.valid;

    if (!this.hasActiveKeyword) {
      toastr.error('Cannot write copy until a keyword is defined for this URL.');
      result = false;
    }

    if (!this.hasValidAccessDetails && this.boostToPublish) {
      toastr.error('If Boostability is going to publish copy, you cannot save this task until CMS or FTP details have been completed.');
      result = false;
    }

    if (this.boostToPublish && this.isCmsNotSet) {
      toastr.error('CMS selection required. Boostability is publishing changes for this site so a CMS is required. Please adjust the customer Publish Options & Access settings before allocating work.');
      result = false;
    }

    if (!result && this.validation && this.validation.errors) {
      this.validation.errors.showAllMessages();
    }
    return result;
  }

  save(workRequest: Boo.Objects.Work.WorkRequest): any {
    WorkRequestHelper.setOnsiteCopyOrderReason(workRequest, this.publishToExistingPage.value ? 'Existing Page' : 'New Page', this.user.UserId);
    WorkRequestHelper.setOnsiteCopyLinkBuildAfterPublish(workRequest, this.linkBuildingAfterPublish(), this.user.UserId);
    WorkRequestHelper.setOnsiteCopyPageType(workRequest, this.onsiteCopyPageType(), this.user.UserId);
    WorkRequestHelper.setOnsiteCopyImplementationInstructions(workRequest, this.implementationInstructions(), this.user.UserId);
    WorkRequestHelper.setCustomDescription(workRequest, this.customDescription(), this.user.UserId);
  }

  implementationOwnerMapToString(implementationOwner: Boo.Objects.Enums.ImplementationOwners): string {
    switch(implementationOwner) {
      case Boo.Objects.Enums.ImplementationOwners.Boostability:
        return "Boostability";
      case Boo.Objects.Enums.ImplementationOwners.Customer:
        return "Customer";
      case Boo.Objects.Enums.ImplementationOwners.Partner:
        return "Partner";
      case Boo.Objects.Enums.ImplementationOwners.Custom:
        return "Custom Email";
      default:
        return "No one";
    }
  }
}